import dayjs from 'dayjs';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { BiCheckCircle } from 'react-icons/bi';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { CircularProgress, IconButton } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AddIcon, BackIcon } from '../../../../../assets/svg';
import { PyButton, PyInput, TOASTER_TYPE, ToastMessage } from '../../../../../components';
import { usePayLinkCart } from '../../../context/PayLinkCartContext';
import { usePayLinkUser } from '../../../context/PaylinkUserContext';
import {
  addShippingAddress, deleteShippingAddress, getCustomerDetails, getShippingAddress,
  getShippingRateFromShipBubble, getShippingRateFromShippo, getStoreProfilePolicy,
  updateUserDetails
} from '../../../services/api';
import { IAddressPayload, IUserContact } from '../../../types/identity';
import {
  IShipBubbleRatePayload, IShipBubbleRateResponse, IShippoRatePayload, TRatesFilterPriority,
  TShippoPreference
} from '../../../types/walllet';

type TInitialValue = {
  firstName: string;
  lastName: string;
  streetName: string;
  houseNumber: string;
  province: string;
  state: string;
  country: string;
  phoneNumber: string;
};

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  streetName: Yup.string().required('Street name is required'),
  houseNumber: Yup.string().required('House no. is required'),
  province: Yup.string().required('Province is required'),
  state: Yup.string().required('State is required'),
  country: Yup.string().required('Country is required'),
  phoneNumber: Yup.string()
    .required('Phone number is required')
    .matches(/^0[789][01]\d{8}$/, 'Enter a valid Nigerian phone number e.g. 08012345678')
    .test(
      'no-country-code',
      'Use local format only (e.g. 08012345678), do not include +234',
      (value) => !value || (!value.startsWith('+234') && !value.startsWith('234')),
    ),
});

interface IProps {
  handleNextView: () => void;
  isDisabled?: boolean;
}

export const ShippingForm: React.FC<IProps> = ({ handleNextView, isDisabled = false }) => {
  const {
    user,
    shippingAddress,
    handleSelectAddress,
    handleSetShippingAddressAndRate,
    deliveryNote,
    setDeliveryNote,
  } = usePayLinkUser();
  const { storeProfile, cartItems, sessionUniqueReferenceNumber, currency } = usePayLinkCart();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<'list' | 'add' | 'edit'>('list');
  const [addressBeingEdited, setAddressBeingEdited] = useState<IUserContact | null>(null);
  const [addressPendingDelete, setAddressPendingDelete] = useState<IUserContact | null>(null);
  const [newlyAddedAddressUrn, setNewlyAddedAddressUrn] = useState<string | null>(null);
  const [ratesFilterPriority, setRatesFilterPriority] =
    useState<TRatesFilterPriority>('Recommended');
  const [fetchedRatesByTab, setFetchedRatesByTab] = useState<
    Record<string, IShipBubbleRateResponse>
  >({});
  const getAddressUrn = (address?: Partial<IUserContact> | null) =>
    address?.uniqueReferenceNumber || address?.userUniqueReferenceNumber || '';
  const selectedAddressUrn = getAddressUrn(shippingAddress);
  const getRateTabKey = (
    addressUrn: string,
    priority: TRatesFilterPriority,
  ) => `${addressUrn}::${priority}`;
  const currentRateTabKey = selectedAddressUrn
    ? getRateTabKey(selectedAddressUrn, ratesFilterPriority)
    : '';
  const fetchedRate = currentRateTabKey
    ? fetchedRatesByTab[currentRateTabKey] || null
    : null;
  const hasFetchedRateForCurrentTab = !!fetchedRate;
  const hasFetchedAnyRateForSelectedAddress = !!selectedAddressUrn &&
    Object.keys(fetchedRatesByTab).some((key) =>
      key.startsWith(`${selectedAddressUrn}::`),
    );

  const rateFilterOptions: TRatesFilterPriority[] = [
    'Cheapest',
    'Fastest',
    'Recommended',
  ];

  const { data: shippingAddresses, isLoading } = useQuery({
    queryKey: ['GetShippingAddress', user?.userUniqueReferenceNumber],
    queryFn: () =>
      getShippingAddress({
        userUniqueReferenceNumber: user?.userUniqueReferenceNumber as string,
      }),
    enabled: !!user?.userUniqueReferenceNumber,
    refetchOnWindowFocus: false,
  });

  const { data: customerDetails } = useQuery({
    queryKey: ['GetCustomerDetails', storeProfile?.customerUniqueReferenceNumber],
    queryFn: () => getCustomerDetails(storeProfile?.customerUniqueReferenceNumber as string),
    enabled: !!storeProfile?.customerUniqueReferenceNumber,
    refetchOnWindowFocus: false,
  });

  const { data: storePolicyResponse } = useQuery({
    queryKey: ['GetStoreProfilePolicy', storeProfile?.customerUniqueReferenceNumber, storeProfile?.merchantUniqueReferenceNumber],
    queryFn: () =>
      getStoreProfilePolicy({
        customerUniqueReferenceNumber: storeProfile?.customerUniqueReferenceNumber as string,
        merchantUniqueReferenceNumber: storeProfile?.merchantUniqueReferenceNumber as string,
      }),
    enabled: !!storeProfile?.customerUniqueReferenceNumber && !!storeProfile?.merchantUniqueReferenceNumber,
    refetchOnWindowFocus: false,
  });

  const storePolicy = storePolicyResponse?.data?.[0] ?? null;

  const onGetShippingRateFromShipBubble = useMutation({
    mutationKey: ['GetShippingRateFromShipBubble'],
    mutationFn: getShippingRateFromShipBubble,
  });

  const onGetShippingRateFromShippo = useMutation({
    mutationKey: ['GetShippingRateFromShippo'],
    mutationFn: getShippingRateFromShippo,
  });

  const onUpdateUserDetails = useMutation({
    mutationKey: ['UpdateUserDetails'],
    mutationFn: updateUserDetails,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['GetUserDetails', user?.userUniqueReferenceNumber],
      });
    },
  });

  const onAddShippingAddress = useMutation({
    mutationKey: ['AddShippingAddress'],
    mutationFn: addShippingAddress,
    onSuccess: (res, variable) => {
      if (!res) return;

      let createdAddress: IUserContact | undefined;

      if (Array.isArray(res)) {
        createdAddress = res.find(
          (address) => getAddressUrn(address) === variable.uniqueReferenceNumber,
        );

        if (!createdAddress) {
          createdAddress = res.find(
            (address) =>
              address.phoneNumber === variable.phoneNumber &&
              address.contact?.address1 === variable.contact.address1 &&
              address.contact?.address2 === variable.contact.address2 &&
              address.contact?.city === variable.contact.city,
          );
        }
      }

      if (createdAddress) {
        handleSelectAddress(createdAddress);
      }

      setNewlyAddedAddressUrn(
        getAddressUrn(createdAddress) || variable.uniqueReferenceNumber,
      );
      handleUpdateUser(variable);
      queryClient.invalidateQueries({
        queryKey: ['GetShippingAddress', user?.userUniqueReferenceNumber],
      });
      setMode('list');
      setAddressBeingEdited(null);
    },
  });

  const onDeleteShippingAddress = useMutation({
    mutationKey: ['DeleteShippingAddress'],
    mutationFn: deleteShippingAddress,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['GetShippingAddress', user?.userUniqueReferenceNumber],
      });
      setAddressPendingDelete(null);
      ToastMessage(TOASTER_TYPE.SUCCESS, 'Shipping address deleted successfully.');
    },
    onError: () => {
      ToastMessage(TOASTER_TYPE.ERROR, 'Unable to delete this address right now. Please try again.');
    },
  });

  const initialValues: TInitialValue = {
    firstName: addressBeingEdited?.firstName ?? user?.firstName ?? '',
    lastName: addressBeingEdited?.lastName ?? user?.lastName ?? '',
    streetName: addressBeingEdited?.contact?.address1 ?? '',
    houseNumber: addressBeingEdited?.contact?.address2 ?? '',
    province: addressBeingEdited?.contact?.city ?? '',
    state: addressBeingEdited?.contact?.state ?? '',
    country: addressBeingEdited?.contact?.country ?? '',
    phoneNumber: addressBeingEdited?.phoneNumber ?? '',
  };

  useEffect(() => {
    if (!shippingAddresses || shippingAddresses.length === 0) return;

    if (newlyAddedAddressUrn) {
      const addedAddress = shippingAddresses.find(
        (address) => getAddressUrn(address) === newlyAddedAddressUrn,
      );

      if (addedAddress) {
        handleSelectAddress(addedAddress);
        setNewlyAddedAddressUrn(null);
        return;
      }
    }

    if (!shippingAddress) {
      handleSelectAddress(shippingAddresses[0]);
      return;
    }

    const existsInList = shippingAddresses.some(
      (address) => getAddressUrn(address) === getAddressUrn(shippingAddress),
    );

    if (!existsInList) {
      handleSelectAddress(shippingAddresses[0]);
    }
  }, [handleSelectAddress, newlyAddedAddressUrn, shippingAddress, shippingAddresses]);

  const handleUpdateUser = (variable: IAddressPayload) => {
    const { firstName, lastName, phoneNumber } = variable;
    onUpdateUserDetails.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phoneNumber,
      dateOfBirth: '2024-04-19T15:05:19.180Z',
      gender: '',
      userUniqueReferenceNumber: user?.userUniqueReferenceNumber ?? '',
    });
  };

  const handleAddressSelection = (address: IUserContact) => {
    handleSelectAddress(address);
  };

  const handleEditAddress = (address: IUserContact) => {
    setAddressBeingEdited(address);
    setMode('edit');
  };

  const handleRequestDeleteAddress = (address: IUserContact) => {
    setAddressPendingDelete(address);
  };

  const handleConfirmDeleteAddress = async () => {
    if (!addressPendingDelete || !user?.userUniqueReferenceNumber) return;

    await onDeleteShippingAddress.mutateAsync({
      userUniqueReferenceNumber: user.userUniqueReferenceNumber,
      addressUniqueReferenceNumber: getAddressUrn(addressPendingDelete),
    });
  };

  const handleGetShippingRateFromShipBubble = async (address: IUserContact) => {
    console.log("address", address, sessionUniqueReferenceNumber)
    if (!address || !sessionUniqueReferenceNumber) return;

    const priorityToUse: TRatesFilterPriority = ratesFilterPriority;

    const vendorCountry = storeProfile?.storeRegionCode?.toLowerCase();
    const isVendorNigeria = vendorCountry === 'nigeria' || vendorCountry === 'ng';
    const buyerCountry = address.contact.country?.toLowerCase();
    const isBuyerNigeria = buyerCountry === 'nigeria' || buyerCountry === 'ng';
    // Use ShipBubble only for domestic (Nigeria vendor → Nigeria buyer); Shippo for international
    const isNigeria = isVendorNigeria && isBuyerNigeria;

    try {
      let res: IShipBubbleRateResponse;

      if (isNigeria) {
        const shipBubblePayload: IShipBubbleRatePayload = {
          destinationAddress: {
            name: `${address.firstName} ${address.lastName}`,
            phone: address.phoneNumber,
            email: address.emailAddress,
            address: `${address.contact.address2} ${address.contact.address1}`,
            city: address.contact.city,
            state: address.contact.state,
            country: address.contact.country,
            latitude: 0,
            longitude: 0,
            postCode: "200231"
          },
          parcel: cartItems.map((item) => ({
            unitAmount: item.price.toString(),
            productName: item.name as string,
            productUniqueReferenceNumber: item.productCatalogueItemUniqueReferenceNumber,
            variantUniqueReferenceNumber: item.variantUniqueReferenceNumber,
            quantity: item.quantity,
            categoryUniqueReferenceNumber: item.categoryUniqueReferenceNumber,
            description: item.categoryName as string,
          })),
          customerUniqueReferenceNumber: storeProfile?.customerUniqueReferenceNumber as string,
          sessionUniqueReferenceNumber,
          ratesFilterPriority: priorityToUse,
          serviceDeliveryType: "pickup",
        };
        res = await onGetShippingRateFromShipBubble.mutateAsync(shipBubblePayload);
      } else {
        // Map our internal filter priority to the ShippoPreference enum values the API accepts.
        // 'Recommended' has no direct equivalent → use 'Balanced'.
        const preferenceMap: Record<TRatesFilterPriority, TShippoPreference> = {
          Cheapest: 'Cheapest',
          Fastest: 'Fastest',
          Recommended: 'Balanced',
        };
        const shippoPayload: IShippoRatePayload = {
          customerUniqueReferenceNumber: storeProfile?.customerUniqueReferenceNumber as string,
          sessionUniqueReferenceNumber,
          senderName: storeProfile?.merchantDetails?.companyName ?? storeProfile?.storeName ?? '',
          // addressFrom: {
          //   name: storeProfile?.merchantDetails?.companyName ?? storeProfile?.storeName ?? '',
          //   street1: '20 Ducie Street', // customerDetails?.company?.businessAddress?.addressLine ?? storeProfile?.merchantDetails?.city ?? '',
          //   city: 'Manchester', // customerDetails?.company?.businessAddress?.city ?? storeProfile?.merchantDetails?.city ?? '',
          //   zip: 'M1 2DP', // customerDetails?.company?.businessAddress?.postalCode ??
          //   country: 'United Kingdom', // customerDetails?.company?.businessAddress?.country ?? storeProfile?.merchantDetails?.country ?? '',
          // },
          addressFrom: {
            name: storeProfile?.merchantDetails?.companyName ?? storeProfile?.storeName ?? '',
            street1: customerDetails?.company?.businessAddress?.addressLine ?? storeProfile?.merchantDetails?.city ?? '',
            city: customerDetails?.company?.businessAddress?.city ?? storeProfile?.merchantDetails?.city ?? '',
            zip: customerDetails?.company?.businessAddress?.postalCode || 'M1 2DP',
            country: customerDetails?.company?.businessAddress?.country ?? storeProfile?.merchantDetails?.country ?? '',
          },
          receiverName: `${address.firstName} ${address.lastName}`,
          addressTo: {
            name: `${address.firstName} ${address.lastName}`,
            street1: `${address.contact.address2} ${address.contact.address1}`.trim(),
            city: address.contact.city,
            zip: 'M1 1JQ',
            country: address.contact.country,
          },
          parcels: cartItems.map((item) => ({
            productCatalogueUniqueReferenceNumber: item.productCatalogueItemUniqueReferenceNumber,
            productCategoryUniqueReferenceNumber: item.categoryUniqueReferenceNumber,
            productCatalogueVariantUniqueReferenceNumber: item.variantUniqueReferenceNumber,
            length: (item as any).length || 1,
            width: (item as any).width || 1,
            height: (item as any).height || 1,
            weight: (item as any).weight || 1,
            quantity: item.quantity,
          })),
          async: true,
          preference: preferenceMap[priorityToUse],
        };
        res = await onGetShippingRateFromShippo.mutateAsync(shippoPayload);
      }

      if (res) {
        const addressUrn = getAddressUrn(address);
        const rateTabKey = getRateTabKey(addressUrn, priorityToUse);

        // Normalise the response so both ShipBubble and Shippo have valid ETA fields.
        // isNigeria tells us whether ShipBubble was used; !isNigeria means Shippo.
        let normalizedRes = { ...res };

        if (!isNigeria) {
          // Shippo: backend may return isAssistedShipping=false even though it IS assisted.
          // Force it to true so the checkout knows to treat this as a Paycurra-managed rate.
          normalizedRes = { ...normalizedRes, isAssistedShipping: true };
        }

        // Apply ETA fallback if deliveryEta or deliveryEtaTime is missing/invalid
        const dispatchDays = storePolicy?.dispatchTimeFrameInDays ?? 3;
        const hasEta = normalizedRes.deliveryEta && normalizedRes.deliveryEta.trim() !== '';
        const hasEtaTime =
          normalizedRes.deliveryEtaTime &&
          dayjs(normalizedRes.deliveryEtaTime).isValid() &&
          dayjs(normalizedRes.deliveryEtaTime).isAfter(dayjs('2000-01-01'));

        if (!hasEta) {
          normalizedRes = {
            ...normalizedRes,
            deliveryEta: `${dispatchDays}-${dispatchDays + 2} business days`,
          };
        }
        if (!hasEtaTime) {
          normalizedRes = {
            ...normalizedRes,
            deliveryEtaTime: dayjs().add(dispatchDays, 'day').toISOString(),
          };
        }

        setFetchedRatesByTab((prev) => ({
          ...prev,
          [rateTabKey]: normalizedRes,
        }));
      }
    } catch {
      ToastMessage(
        TOASTER_TYPE.ERROR,
        "We couldn't find delivery options for this address. Please check that your address is correct, or try adding a new one.",
      );
    }
  };

  const handleContinueWithFetchedRate = () => {
    if (!shippingAddress || !fetchedRate) return;

    // isAssistedShipping is set by the API on the rate response:
    // true  → Paycurra-facilitated shipping (ShipBubble / Shippo)
    // false → merchant-configured custom rate
    handleSetShippingAddressAndRate({
      shippingRateUniqueReferenceNumber: fetchedRate.rateUniqueReferenceNumber ?? "",
      addressUniqueReferenceNumber: getAddressUrn(shippingAddress),
      charge: fetchedRate.total ?? fetchedRate.price ?? 0,
      country: shippingAddress.contact.country,
      province: shippingAddress.contact.state,
      location: `${shippingAddress.contact.address2} ${shippingAddress.contact.address1}`,
      city: shippingAddress.contact.city,
      isAssistedShipping: fetchedRate.isAssistedShipping,
      // isAssistedShipping: true,
      courierId: fetchedRate.courierId,
      deliveryEta: fetchedRate.deliveryEta,
      deliveryEtaTime: fetchedRate.deliveryEtaTime,
      pickupEta: fetchedRate.pickupEta,
      pickupEtaTime: fetchedRate.pickupEtaTime,
    });
    handleNextView();
  };

  useEffect(() => {
    if (!selectedAddressUrn) return;

    // Reset to default tab for every new selected address.
    setRatesFilterPriority('Recommended');
  }, [selectedAddressUrn]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center !gap-4 !py-20">
        <CircularProgress />
      </div>
    );
  }

  return (
    <section
      className={`flex flex-col gap-4 w-full !pt-6 border-t border-py_black-100 ${isDisabled ? 'pointer-events-none opacity-70' : ''
        }`}
    >
      {mode === 'list' && shippingAddresses && shippingAddresses.length > 0 && (
        <Formik enableReinitialize initialValues={{}} onSubmit={() => { }}>
          {() => (
            <form className="flex flex-col !gap-6 w-full">
              <div className="flex flex-col !gap-4 w-full">
                <p className="text-base text-py_black-950 font-normal leading-[140%]">
                  Choose, change or add a shipping address
                </p>

                <div className="flex flex-col gap-2">
                  {shippingAddresses.map((address) => {
                    const isSelected =
                      getAddressUrn(shippingAddress) === getAddressUrn(address);

                    return (
                      <div
                        key={getAddressUrn(address)}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleAddressSelection(address)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleAddressSelection(address);
                          }
                        }}
                        className={`group relative w-full text-left p-3 rounded-lg border transition-all flex items-start gap-3 text-sm cursor-pointer ${isSelected
                          ? '!border-py_green_2-500 bg-py_black-50 shadow-[2px_2px_8px_0px_rgba(5,128,113,0.15)]'
                          : 'border-py_black-100 hover:border-py_black-300'
                          }`}
                      >
                        {/* <span
                          className={`mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                            isSelected
                              ? 'border-py_green_2-600 bg-py_green_2-600'
                              : 'border-py_black-300'
                          }`}
                          aria-hidden="true"
                        >
                          {isSelected && (
                            <span className="h-2 w-2 rounded-full bg-py_green-600" />
                          )}
                        </span> */}

                        <BiCheckCircle className={`mt-0.5 h-4 w-4 flex-shrink-0 ${isSelected ? 'text-py_green_2-600' : 'text-py_black-300'}`} />

                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-semibold text-py_black-950 leading-tight truncate">
                            {address.firstName} {address.lastName}
                          </p>
                          <p className="text-[12px] text-py_black-700 leading-snug truncate">
                            {address.contact.address2}, {address.contact.address1}
                          </p>
                          <p className="text-[12px] text-py_black-700 leading-snug truncate">
                            {address.contact.city}, {address.contact.state}, {address.contact.country}
                          </p>
                          <p className="text-[12px] text-py_black-600 leading-snug">
                            {address.phoneNumber}
                          </p>
                        </div>

                        <div className="inline-flex items-center gap-0.5">
                          <IconButton
                            size="small"
                            className="!p-1 !text-py_black-500 hover:!text-py_black-950"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditAddress(address);
                            }}
                            aria-label="Edit address"
                          >
                            <FiEdit2 size={13} />
                          </IconButton>
                          <IconButton
                            size="small"
                            className="!p-1 !text-py_black-500 hover:!text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRequestDeleteAddress(address);
                            }}
                            aria-label="Delete address"
                          >
                            <FiTrash2 size={13} />
                          </IconButton>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </form>
          )}
        </Formik>
      )}

      {(mode !== 'list' || (shippingAddresses && shippingAddresses.length === 0)) && (
        <>
          <div className="flex flex-col gap-1">
            <div className="inline-flex items-center gap-1">
              {shippingAddresses && shippingAddresses.length > 0 && mode !== 'list' && (
                <IconButton
                  onClick={() => {
                    setMode('list');
                    setAddressBeingEdited(null);
                  }}
                  className="!p-1 !rounded-full"
                >
                  <BackIcon />
                </IconButton>
              )}
              <h3 className="text-base font-medium leading-[140%] tracking-[-0.02em]">
                {mode === 'edit' ? 'Edit address' : 'Shipping details'}
              </h3>
            </div>
            <p className="text-sm text-py_black-600">
              {mode === 'edit'
                ? 'Update the address details below'
                : 'Provide the address you want your order to be shipped to'}
            </p>
          </div>

          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values) => {
              const isEditMode = mode === 'edit' && !!addressBeingEdited;
              const uniqueReferenceNumber = isEditMode
                ? addressBeingEdited.userUniqueReferenceNumber
                : uuidv4();

              const payload = {
                uniqueReferenceNumber,
                userUniqueReferenceNumber: user?.userUniqueReferenceNumber as string,
                emailAddress: user?.email as string,
                firstName: values.firstName,
                lastName: values.lastName,
                contactKind: 'Shipping',
                phoneNumber: values.phoneNumber,
                contact: {
                  address1: values.streetName,
                  address2: values.houseNumber.toString(),
                  city: values.province,
                  state: values.state,
                  country: values.country,
                },
              };

              setNewlyAddedAddressUrn(uniqueReferenceNumber);
              onAddShippingAddress.mutate(payload as any);
            }}
          >
            {({ handleBlur, handleSubmit, handleChange, values }) => (
              <form onSubmit={handleSubmit}>
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <PyInput
                      fullWidth
                      label="First name"
                      type="text"
                      name="firstName"
                      placeholder="Enter first name"
                      value={values.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="flex-1">
                    <PyInput
                      fullWidth
                      label="Last name"
                      type="text"
                      name="lastName"
                      placeholder="Enter last name"
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2 mb-6">
                  <PyInput
                    fullWidth
                    label="Phone number"
                    type="tel"
                    name="phoneNumber"
                    placeholder="08012345678"
                    value={values.phoneNumber}
                    inputProps={{
                      maxLength: 11,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 11);
                      handleChange({
                        target: {
                          name: 'phoneNumber',
                          value,
                        },
                      });
                    }}
                    onBlur={handleBlur}
                  />

                  <PyInput
                    fullWidth
                    label="Street name"
                    type="text"
                    name="streetName"
                    placeholder="Street name"
                    value={values.streetName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <PyInput
                        fullWidth
                        label="House no."
                        type="number"
                        name="houseNumber"
                        placeholder="House no."
                        value={values.houseNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                    <div className="flex-1">
                      <PyInput
                        fullWidth
                        label="Town/Province"
                        type="text"
                        name="province"
                        placeholder="Town/Province"
                        value={values.province}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <PyInput
                        fullWidth
                        label="State"
                        type="text"
                        name="state"
                        placeholder="State"
                        value={values.state}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                    <div className="flex-1">
                      <PyInput
                        fullWidth
                        label="Country"
                        type="text"
                        name="country"
                        placeholder="Country"
                        value={values.country}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-5">
                  <PyButton
                    loading={onAddShippingAddress.isLoading}
                    isDisabled={isDisabled}
                    extraClass="!px-8 !w-full lg:!w-fit"
                    type="submit"
                  >
                    {mode === 'edit' ? 'Save changes' : 'Add Shipping Address'}
                  </PyButton>
                </div>
              </form>
            )}
          </Formik>
        </>
      )}

      {mode === 'list' && shippingAddress && (
        <div className="flex flex-col gap-4 w-full">
          {hasFetchedAnyRateForSelectedAddress && (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-py_black-700">Rate priority</p>
              <div className="inline-flex items-center rounded-lg border border-py_black-100 p-1 gap-1 w-fit">
                {rateFilterOptions.map((option) => {
                  const isActive = ratesFilterPriority === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setRatesFilterPriority(option)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${isActive
                        ? 'bg-py_green_2-100 text-py_green_2-700'
                        : 'text-py_black-600 hover:bg-py_black-50'
                        }`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {hasFetchedRateForCurrentTab && (
            <div className="rounded-xl flex flex-col gap-3">
              <p className="text-sm text-py_black-950 font-semibold">
                Fetched rate ({ratesFilterPriority})
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-lg border border-py_black-100 bg-white px-3 py-2">
                  <p className="text-[11px] text-py_black-500">Courier</p>
                  <p className="text-sm text-py_black-900 font-medium">{fetchedRate.courierName}</p>
                </div>
                <div className="rounded-lg border border-py_black-100 bg-white px-3 py-2">
                  <p className="text-[11px] text-py_black-500">Delivery charge</p>
                  <p className="text-sm text-py_black-900 font-medium">
                    {fetchedRate.currency === 'GBP' ? '£' : fetchedRate.currency === 'NGN' ? '₦' : (currency || '₦')}
                    {(fetchedRate.total ?? fetchedRate.price ?? 0).toLocaleString()}
                  </p>
                </div>
                <div className="rounded-lg border border-py_black-100 bg-white px-3 py-2">
                  <p className="text-[11px] text-py_black-500">Delivery ETA time</p>
                  <p className="text-sm text-py_black-900 font-medium">
                    {dayjs(fetchedRate.deliveryEtaTime).isValid()
                      ? dayjs(fetchedRate.deliveryEtaTime).format('DD/MM/YYYY, h:mm A')
                      : fetchedRate.deliveryEtaTime}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {mode === 'list' && shippingAddresses && shippingAddresses.length > 0 && (
        <div className="flex flex-col !gap-4 w-full">
          <p className="text-base text-py_black-950 font-normal leading-[140%]">
            Add a delivery note (Optional)
          </p>

          <Formik
            enableReinitialize
            initialValues={{ deliveryNote: deliveryNote || '' }}
            onSubmit={() => { }}
          >
            {({ values, setFieldValue }) => (
              <PyInput
                fullWidth
                isMultiline
                rows={3}
                label="Delivery note"
                placeholder="Call Bella for availability on delivery date. Phone contact - 901 086 0097"
                name="deliveryNote"
                value={values.deliveryNote}
                onChange={(e) => {
                  setFieldValue('deliveryNote', e.target.value);
                  setDeliveryNote(e.target.value);
                }}
              />
            )}
          </Formik>

          <PyButton
            click={() => {
              setAddressBeingEdited(null);
              setMode('add');
            }}
            isDisabled={isDisabled}
            variant="SecondaryOutline"
            extraClass="!h-10 !w-fit"
          >
            <AddIcon />
            Add another shipping address
          </PyButton>
        </div>
      )}

      {mode === 'list' && !hasFetchedRateForCurrentTab && (
        <div className="flex items-center gap-5">
          <PyButton
            click={() => {
              if (!shippingAddress) {
                ToastMessage(
                  TOASTER_TYPE.ERROR,
                  'Please select or add a shipping address first.',
                );
                return;
              }

              void handleGetShippingRateFromShipBubble(shippingAddress);
            }}
            loading={onGetShippingRateFromShipBubble.isLoading || onGetShippingRateFromShippo.isLoading}
            isDisabled={isDisabled || !shippingAddress}
            extraClass="!px-8 !w-full"
          >
            {!(onGetShippingRateFromShipBubble.isLoading || onGetShippingRateFromShippo.isLoading) && 'Get shipping and payment options'}
          </PyButton>
        </div>
      )}

      {mode === 'list' && (
        <div className="flex items-center gap-5">
          <PyButton
            click={handleContinueWithFetchedRate}
            isDisabled={isDisabled || !hasFetchedRateForCurrentTab}
            extraClass="!px-8 !w-full lg:!w-fit"
          >
            Continue
          </PyButton>
        </div>
      )}

      {addressPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-py_black-950">Delete address</h3>
            <p className="mt-2 text-sm text-py_black-600 leading-[150%]">
              Are you sure you want to delete this shipping address? This action cannot be undone.
            </p>

            <div className="mt-6 flex items-center justify-end gap-3">
              <PyButton
                variant="SecondaryOutline"
                isDisabled={isDisabled}
                click={() => setAddressPendingDelete(null)}
              >
                Cancel
              </PyButton>
              <PyButton
                loading={onDeleteShippingAddress.isLoading}
                isDisabled={isDisabled}
                click={handleConfirmDeleteAddress}
              >
                Delete
              </PyButton>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
